// FILE: apps/admin/src/pages/Catalog.tsx
// VERSION: 1.1.0
// START_MODULE_CONTRACT
//   PURPOSE: Admin Catalog & B2B API curation page displaying 4-gateway wholesale balances (PlayWallet, MooGold, GamesDrop, PikaSim), provider sync selector, category default markups, bulk product/SKU publication controls, LOCAL_STOCK encrypted PIN pool uploader, and searchable product/SKU curation table with live RUB/GRAM margin preview.
//   SCOPE: AdminOfferMarginPreview, BulkCatalogSelectionSummary, parsePercentInputToBp, computeAdminOfferPreview, resolveBulkCatalogSelection, chunkCatalogProductIds, and Catalog React page component.
//   DEPENDS: ../api.js, ../components/Icons.js, ../components/VoucherPoolModal.js, ../toast.js, ./Orders.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   AdminOfferMarginPreview - Computed wholesale USD/RUB, retail RUB/GRAM, and net margin preview for a catalog SKU
//   BulkCatalogSelectionSummary - Resolved multi-select and filtered target product IDs for bulk catalog publication actions
//   Catalog - Admin SPA page for curating PlayWallet, MooGold, GamesDrop, PikaSim, and LOCAL_STOCK storefront products, SKUs, markups, and instructions
//   chunkCatalogProductIds - Split bulk-publish product ID arrays into fixed-size batches to avoid HTTP 413 payload limits
//   computeAdminOfferPreview - Pure helper computing live retail RUB/GRAM and net margin from wholesale USD micros, FX, and markup hierarchy
//   parsePercentInputToBp - Parse operator percent string input into integer basis points (or null when empty)
//   resolveBulkCatalogSelection - Resolve explicit row checkbox selection vs all filtered catalog product IDs
// END_MODULE_MAP

import {
  type AdminBulkPublishCatalogResponse,
  type AdminBulkPublishMode,
  type AdminCatalogCategoryDto,
  type AdminCatalogOfferDto,
  type AdminCatalogOverviewDto,
  type AdminCatalogProductDto,
  type AdminCatalogProductsResponse,
  type AdminTriggerSyncRequest,
  type AdminTriggerSyncResponse,
} from '@brainrotstore/api-contracts';
import {
  computeCatalogOfferPrice,
  milliToGram,
  resolveEffectiveMarkupBp,
} from '@brainrotstore/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { createPortal } from 'react-dom';

import { api, dt, num } from '../api.js';
import { AdminIcon } from '../components/Icons.js';
import {
  VoucherPoolModal,
  type VoucherPoolModalTarget,
} from '../components/VoucherPoolModal.js';
import { useToast } from '../toast.js';
import { formatProviderBadgeLabel } from './Orders.js';

export interface AdminOfferMarginPreview {
  effectiveMarkupBp: number;
  wholesaleUsd: number;
  wholesaleRub: number;
  retailRub: number;
  retailGram: number;
  marginRub: number;
  marginPercent: number;
}

// START_CONTRACT: parsePercentInputToBp
//   PURPOSE: Convert an operator percentage string (e.g. "18.5") into clamped basis points (1850) or null if blank.
//   INPUTS: raw: string
//   OUTPUTS: number | null
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: parsePercentInputToBp
export function parsePercentInputToBp(raw: string): number | null {
  // START_BLOCK_PARSE_PERCENT_INPUT_TO_BP
  const trimmed = raw.trim().replace(',', '.');
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.min(50_000, Math.round(parsed * 100));
  // END_BLOCK_PARSE_PERCENT_INPUT_TO_BP
}

// START_CONTRACT: computeAdminOfferPreview
//   PURPOSE: Compute live wholesale USD/RUB, retail RUB/GRAM, and net RUB/% margin for an offer row in Admin UI.
//   INPUTS: { wholesaleUsdMicros, usdRub, gramRub, offerOverrideMarkupBp, productMarkupBp, categoryDefaultMarkupBp, minAmountRub?, providerRateBp? }
//   OUTPUTS: AdminOfferMarginPreview
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: computeAdminOfferPreview
export function computeAdminOfferPreview(input: {
  wholesaleUsdMicros: number;
  usdRub: number;
  gramRub: number;
  offerOverrideMarkupBp: number | null | undefined;
  productMarkupBp: number | null | undefined;
  categoryDefaultMarkupBp: number | null | undefined;
  minAmountRub?: number | null;
  providerRateBp?: number | null;
}): AdminOfferMarginPreview {
  // START_BLOCK_COMPUTE_ADMIN_OFFER_PREVIEW
  const effectiveMarkupBp = resolveEffectiveMarkupBp(
    input.offerOverrideMarkupBp,
    input.productMarkupBp,
    input.categoryDefaultMarkupBp,
  );
  const priced = computeCatalogOfferPrice({
    wholesaleUsdMicros: input.wholesaleUsdMicros,
    usdRub: input.usdRub,
    gramRub: input.gramRub,
    markupBp: effectiveMarkupBp,
    customAmountRub: input.minAmountRub ?? null,
    providerRateBp: input.providerRateBp ?? null,
  });
  const wholesaleUsd = Math.round((input.wholesaleUsdMicros / 1_000_000) * 100) / 100;
  const retailGram = Math.round(milliToGram(priced.retailMilliGram) * 100) / 100;
  const marginRub = Math.round((priced.retailRub - priced.wholesaleRub) * 100) / 100;
  const marginPercent =
    priced.wholesaleRub > 0
      ? Math.round(((priced.retailRub - priced.wholesaleRub) / priced.wholesaleRub) * 10_000) / 100
      : 0;

  return {
    effectiveMarkupBp,
    wholesaleUsd,
    wholesaleRub: priced.wholesaleRub,
    retailRub: priced.retailRub,
    retailGram,
    marginRub,
    marginPercent,
  };
  // END_BLOCK_COMPUTE_ADMIN_OFFER_PREVIEW
}

const bpToPercentStr = (bp: number | null | undefined): string =>
  bp == null ? '' : String(Math.round(bp) / 100);

export interface BulkCatalogSelectionSummary {
  visibleIds: string[];
  selectedVisibleIds: string[];
  targetIds: string[];
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  usingExplicitSelection: boolean;
}

// START_CONTRACT: resolveBulkCatalogSelection
//   PURPOSE: Resolve which visible catalog product IDs are targeted by bulk publish/unpublish actions (checked rows if any, otherwise all visible filtered rows).
//   INPUTS: visibleProducts: ReadonlyArray<{ id: string }>, selectedIdsMap: Record<string, boolean>
//   OUTPUTS: BulkCatalogSelectionSummary
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: resolveBulkCatalogSelection
export function resolveBulkCatalogSelection(
  visibleProducts: ReadonlyArray<{ id: string }>,
  selectedIdsMap: Record<string, boolean>,
): BulkCatalogSelectionSummary {
  // START_BLOCK_RESOLVE_BULK_CATALOG_SELECTION
  const visibleIds = visibleProducts.map((p) => p.id);
  const selectedVisibleIds = visibleIds.filter((id) => Boolean(selectedIdsMap[id]));
  const usingExplicitSelection = selectedVisibleIds.length > 0;
  const targetIds = usingExplicitSelection ? selectedVisibleIds : visibleIds;
  const allVisibleSelected =
    visibleIds.length > 0 && selectedVisibleIds.length === visibleIds.length;
  const someVisibleSelected =
    selectedVisibleIds.length > 0 && selectedVisibleIds.length < visibleIds.length;

  return {
    visibleIds,
    selectedVisibleIds,
    targetIds,
    allVisibleSelected,
    someVisibleSelected,
    usingExplicitSelection,
  };
  // END_BLOCK_RESOLVE_BULK_CATALOG_SELECTION
}

const DEFAULT_BULK_CHUNK_SIZE = 500;
const INITIAL_RENDER_LIMIT = 150;
const RENDER_LIMIT_STEP = 250;

// START_CONTRACT: chunkCatalogProductIds
//   PURPOSE: Partition a large array of product IDs into bounded batches so bulk-publish requests stay well below reverse-proxy and JSON body size limits.
//   INPUTS: ids: ReadonlyArray<string>, chunkSize?: number
//   OUTPUTS: string[][]
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: chunkCatalogProductIds
export function chunkCatalogProductIds(
  ids: ReadonlyArray<string>,
  chunkSize: number = DEFAULT_BULK_CHUNK_SIZE,
): string[][] {
  // START_BLOCK_CHUNK_CATALOG_PRODUCT_IDS
  const safeSize = Math.max(1, Math.floor(chunkSize));
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += safeSize) {
    chunks.push(ids.slice(i, i + safeSize));
  }
  return chunks;
  // END_BLOCK_CHUNK_CATALOG_PRODUCT_IDS
}

// START_CONTRACT: Catalog
//   PURPOSE: Render the Admin Catalog & B2B API curation control plane screen.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Fetches and updates /admin/catalog/* endpoints
//   LINKS: M-ADMIN
// END_CONTRACT: Catalog
export function Catalog() {
  // START_BLOCK_ADMIN_CATALOG_PAGE
  const toast = useToast();
  const qc = useQueryClient();

  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [providerFilter, setProviderFilter] = useState('ALL');
  const [syncProvider, setSyncProvider] =
    useState<AdminTriggerSyncRequest['provider']>('ALL');
  const [publishedOnly, setPublishedOnly] = useState(false);
  const [q, setQ] = useState('');
  const [renderLimit, setRenderLimit] = useState(INITIAL_RENDER_LIMIT);
  const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({});
  const [selectedProducts, setSelectedProducts] = useState<Record<string, boolean>>({});
  const [editingProduct, setEditingProduct] = useState<AdminCatalogProductDto | null>(null);
  const [voucherModalTarget, setVoucherModalTarget] =
    useState<VoucherPoolModalTarget | null>(null);
  const [categoryDraftBp, setCategoryDraftBp] = useState<Record<string, string>>({});
  const [offerDrafts, setOfferDrafts] = useState<
    Record<string, { titleRu: string; markupPct: string }>
  >({});

  const overviewQuery = useQuery({
    queryKey: ['admin-catalog-overview'],
    queryFn: () => api.get<AdminCatalogOverviewDto>('/admin/catalog/overview'),
    refetchInterval: 30_000,
  });

  const productsQuery = useQuery({
    queryKey: ['admin-catalog-products', categoryFilter, providerFilter, publishedOnly, q],
    queryFn: () => {
      const params = new URLSearchParams();
      if (categoryFilter !== 'ALL') params.set('category', categoryFilter);
      if (providerFilter !== 'ALL') params.set('provider', providerFilter);
      if (publishedOnly) params.set('publishedOnly', 'true');
      if (q.trim()) params.set('q', q.trim());
      return api.get<AdminCatalogProductsResponse>(`/admin/catalog/products?${params.toString()}`);
    },
  });

  const syncMut = useMutation({
    mutationFn: (provider: AdminTriggerSyncRequest['provider']) =>
      api.post<AdminTriggerSyncResponse>('/admin/catalog/sync', { provider }),
    onSuccess: (res) => {
      toast(`Синхронизировано: ${res.syncedProducts} товаров, ${res.syncedOffers} SKU`);
      void qc.invalidateQueries({ queryKey: ['admin-catalog-overview'] });
      void qc.invalidateQueries({ queryKey: ['admin-catalog-products'] });
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Ошибка синхронизации'),
  });

  const categoryMut = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { defaultMarkupBp?: number; isPublished?: boolean };
    }) => api.put<AdminCatalogCategoryDto>(`/admin/catalog/categories/${id}`, patch),
    onSuccess: () => {
      toast('Категория обновлена');
      void qc.invalidateQueries({ queryKey: ['admin-catalog-overview'] });
      void qc.invalidateQueries({ queryKey: ['admin-catalog-products'] });
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Ошибка сохранения категории'),
  });

  const productMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.put<AdminCatalogProductDto>(`/admin/catalog/products/${id}`, patch),
    onSuccess: () => {
      toast('Товар обновлён');
      void qc.invalidateQueries({ queryKey: ['admin-catalog-overview'] });
      void qc.invalidateQueries({ queryKey: ['admin-catalog-products'] });
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Ошибка сохранения товара'),
  });

  const offerMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.put<AdminCatalogOfferDto>(`/admin/catalog/offers/${id}`, patch),
    onSuccess: () => {
      toast('SKU обновлён');
      void qc.invalidateQueries({ queryKey: ['admin-catalog-overview'] });
      void qc.invalidateQueries({ queryKey: ['admin-catalog-products'] });
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Ошибка сохранения SKU'),
  });

  const bulkMut = useMutation({
    mutationFn: async (payload: { productIds: string[]; mode: AdminBulkPublishMode }) => {
      const batches = chunkCatalogProductIds(payload.productIds, DEFAULT_BULK_CHUNK_SIZE);
      const totals: AdminBulkPublishCatalogResponse = {
        updatedProducts: 0,
        publishedOffers: 0,
        unpublishedOffers: 0,
      };
      for (const batchIds of batches) {
        const part = await api.post<AdminBulkPublishCatalogResponse>(
          '/admin/catalog/bulk-publish',
          {
            productIds: batchIds,
            mode: payload.mode,
          },
        );
        totals.updatedProducts += part.updatedProducts;
        totals.publishedOffers += part.publishedOffers;
        totals.unpublishedOffers += part.unpublishedOffers;
      }
      return totals;
    },
    onSuccess: (res, vars) => {
      if (vars.mode === 'unpublish_all') {
        toast(
          `Снято с витрины: ${num(res.updatedProducts)} тов. (${num(res.unpublishedOffers)} SKU)`,
        );
      } else if (vars.mode === 'best_offers') {
        toast(
          `Опубликовано: ${num(res.updatedProducts)} тов., ${num(res.publishedOffers)} лучших SKU` +
            (res.unpublishedOffers > 0 ? ` (скрыто дублей: ${num(res.unpublishedOffers)})` : ''),
        );
      } else {
        toast(
          `Опубликовано: ${num(res.updatedProducts)} тов., ${num(res.publishedOffers)} активных SKU`,
        );
      }
      void qc.invalidateQueries({ queryKey: ['admin-catalog-overview'] });
      void qc.invalidateQueries({ queryKey: ['admin-catalog-products'] });
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Ошибка массовой публикации'),
  });

  const overview = overviewQuery.data;
  const productsData = productsQuery.data;
  const visibleProducts = productsData?.products ?? [];
  const displayedProducts = visibleProducts.slice(0, renderLimit);
  const selection = resolveBulkCatalogSelection(visibleProducts, selectedProducts);
  const usdRub = productsData?.usdRub ?? 96;
  const gramRub = productsData?.gramRub ?? 120;
  const categoriesBySlug = new Map(
    (overview?.categories ?? []).map((c) => [c.slug, c]),
  );

  const toggleExpand = (productId: string) => {
    setExpandedProducts((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  const toggleSelectAllVisible = () => {
    if (selection.allVisibleSelected) {
      setSelectedProducts((prev) => {
        const next = { ...prev };
        for (const id of selection.visibleIds) {
          delete next[id];
        }
        return next;
      });
      return;
    }
    setSelectedProducts((prev) => {
      const next = { ...prev };
      for (const id of selection.visibleIds) {
        next[id] = true;
      }
      return next;
    });
  };

  const toggleSelectOne = (productId: string, checked: boolean) => {
    setSelectedProducts((prev) => {
      const next = { ...prev };
      if (checked) {
        next[productId] = true;
      } else {
        delete next[productId];
      }
      return next;
    });
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Каталог и B2B API</h1>
          <div className="page-subtitle">
            Brand Hub витрина: Play Wallet (Steam РФ/СНГ), MooGold (игры + гифт-карты), Свой склад РФ, GamesDrop и PikaSim eSIM
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn ghost"
            onClick={() => setVoucherModalTarget({ mode: 'create' })}
          >
            <span>+ Свой SKU (Склад РФ)</span>
          </button>
          <select
            aria-label="Шлюз синхронизации прайса"
            value={syncProvider}
            onChange={(e) =>
              setSyncProvider(e.target.value as AdminTriggerSyncRequest['provider'])
            }
            style={{ width: 185 }}
          >
            <option value="ALL">Все шлюзы (ALL)</option>
            <option value="PLAYWALLET">Play Wallet (Steam)</option>
            <option value="MOOGOLD">MooGold (UID + Карты)</option>
            <option value="GAMESDROP">GamesDrop.io</option>
            <option value="PIKASIM">PikaSim (eSIM)</option>
          </select>
          <button
            type="button"
            className="btn primary"
            disabled={syncMut.isPending}
            onClick={() => syncMut.mutate(syncProvider)}
          >
            <AdminIcon name="refresh" size={14} />
            <span>{syncMut.isPending ? 'Синхронизация…' : 'Синхронизировать прайс'}</span>
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div className="card">
          <div className="muted" style={{ fontSize: 12 }}>
            Баланс Play Wallet
          </div>
          <div className="mono" style={{ fontSize: 22, fontWeight: 700, marginTop: 6 }}>
            {overview?.playWalletBalanceUsd != null
              ? `$${overview.playWalletBalanceUsd.toFixed(2)}`
              : '—'}
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            Steam РФ/СНГ по логину (Bybit UID 0%)
          </div>
        </div>

        <div className="card">
          <div className="muted" style={{ fontSize: 12 }}>
            Баланс MooGold
          </div>
          <div className="mono" style={{ fontSize: 22, fontWeight: 700, marginTop: 6 }}>
            {overview?.mooGoldBalanceUsd != null
              ? `$${overview.mooGoldBalanceUsd.toFixed(2)}`
              : '—'}
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            UID игры + гифт-карты US/TR/EU/IN
          </div>
        </div>

        <div className="card">
          <div className="muted" style={{ fontSize: 12 }}>
            Баланс GamesDrop.io
          </div>
          <div className="mono" style={{ fontSize: 22, fontWeight: 700, marginTop: 6 }}>
            {overview?.gamesDropBalanceUsd != null
              ? `$${overview.gamesDropBalanceUsd.toFixed(2)}`
              : '—'}
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            Резервные ваучеры и пополнение
          </div>
        </div>

        <div className="card">
          <div className="muted" style={{ fontSize: 12 }}>
            Баланс PikaSim
          </div>
          <div className="mono" style={{ fontSize: 22, fontWeight: 700, marginTop: 6 }}>
            {overview?.pikaSimBalanceUsd != null ? `$${overview.pikaSimBalanceUsd.toFixed(2)}` : '—'}
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            Туристические eSIM (190+ стран)
          </div>
        </div>

        <div className="card">
          <div className="muted" style={{ fontSize: 12 }}>
            Витрина и курсы FX
          </div>
          <div className="mono" style={{ fontSize: 17, fontWeight: 700, marginTop: 6 }}>
            {num(overview?.counts.publishedProducts ?? 0)} / {num(overview?.counts.totalProducts ?? 0)}{' '}
            <span className="muted" style={{ fontSize: 12, fontWeight: 500 }}>
              тов. ({num(overview?.counts.publishedOffers ?? 0)} SKU)
            </span>
          </div>
          <div className="muted mono" style={{ fontSize: 11, marginTop: 5 }}>
            1$ = {usdRub.toFixed(2)} ₽ · GRAM = {gramRub.toFixed(2)} ₽ ·{' '}
            {overview?.lastSyncedAt ? dt(overview.lastSyncedAt) : 'нет синхр.'}
          </div>
        </div>
      </div>

      {overview?.categories && overview.categories.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div
            className="row"
            style={{ justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}
          >
            <strong>Базовые наценки категорий по умолчанию</strong>
            <span className="muted" style={{ fontSize: 12 }}>
              Приоритет наценки: индивидуальный % SKU → % товара → базовый % категории
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 10,
            }}
          >
            {overview.categories.map((cat) => {
              const draftVal = categoryDraftBp[cat.id] ?? bpToPercentStr(cat.defaultMarkupBp);
              return (
                <div
                  key={cat.id}
                  style={{
                    border: '1px solid var(--border, rgba(255,255,255,0.08))',
                    borderRadius: 10,
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div className="row" style={{ justifyContent: 'space-between', gap: 6 }}>
                    <span style={{ fontWeight: 600 }}>{cat.titleRu}</span>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={cat.isPublished}
                        onChange={(e) =>
                          categoryMut.mutate({
                            id: cat.id,
                            patch: { isPublished: e.target.checked },
                          })
                        }
                      />
                      <span>Витрина</span>
                    </label>
                  </div>
                  <div className="row" style={{ gap: 6 }}>
                    <input
                      aria-label={`Наценка категории ${cat.titleRu} (%)`}
                      style={{ width: 90 }}
                      value={draftVal}
                      onChange={(e) =>
                        setCategoryDraftBp((prev) => ({ ...prev, [cat.id]: e.target.value }))
                      }
                      placeholder="15"
                    />
                    <span className="muted" style={{ fontSize: 12 }}>
                      %
                    </span>
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() => {
                        const bp = parsePercentInputToBp(draftVal) ?? 0;
                        categoryMut.mutate({ id: cat.id, patch: { defaultMarkupBp: bp } });
                      }}
                    >
                      Сохранить
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
        <select
          aria-label="Фильтр категории каталога"
          style={{ flex: '0 1 190px' }}
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setRenderLimit(INITIAL_RENDER_LIMIT);
          }}
        >
          <option value="ALL">Все категории</option>
          {(overview?.categories ?? []).map((c) => (
            <option key={c.id} value={c.slug}>
              {c.titleRu}
            </option>
          ))}
        </select>

        <select
          aria-label="Фильтр провайдера каталога"
          style={{ flex: '0 1 185px' }}
          value={providerFilter}
          onChange={(e) => {
            setProviderFilter(e.target.value);
            setRenderLimit(INITIAL_RENDER_LIMIT);
          }}
        >
          <option value="ALL">Все провайдеры</option>
          <option value="PLAYWALLET">Play Wallet (Steam)</option>
          <option value="MOOGOLD">MooGold</option>
          <option value="LOCAL_STOCK">Свой склад (ПИН-пул)</option>
          <option value="GAMESDROP">GamesDrop.io</option>
          <option value="PIKASIM">PikaSim (eSIM)</option>
        </select>

        <input
          style={{ flex: '1 1 240px', minWidth: 180 }}
          placeholder="Поиск по бренду, названию или внешнему SKU…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setRenderLimit(INITIAL_RENDER_LIMIT);
          }}
        />

        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            cursor: 'pointer',
            padding: '0 6px',
          }}
        >
          <input
            type="checkbox"
            checked={publishedOnly}
            onChange={(e) => {
              setPublishedOnly(e.target.checked);
              setRenderLimit(INITIAL_RENDER_LIMIT);
            }}
          />
          <span>Только на витрине</span>
        </label>
      </div>

      {productsQuery.isLoading ? (
        <div className="muted">Загрузка позиций каталога…</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {visibleProducts.length > 0 && (
            <div
              className="row"
              style={{
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: '1px solid var(--hair)',
                background: 'var(--bg-2)',
                gap: 10,
              }}
            >
              <div className="row" style={{ gap: 10 }}>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label="Выбрать все товары по фильтру"
                    checked={selection.allVisibleSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = selection.someVisibleSelected;
                    }}
                    onChange={toggleSelectAllVisible}
                  />
                  <span>Выбрать все ({num(visibleProducts.length)})</span>
                </label>

                {selection.usingExplicitSelection ? (
                  <>
                    <span className="chip info" style={{ fontSize: 11.5 }}>
                      Отмечено: {num(selection.selectedVisibleIds.length)} из{' '}
                      {num(visibleProducts.length)}
                    </span>
                    <button
                      type="button"
                      className="btn sm ghost"
                      onClick={() => setSelectedProducts({})}
                    >
                      Сбросить выбор
                    </button>
                  </>
                ) : (
                  <span className="muted" style={{ fontSize: 12 }}>
                    Действие применится ко всем {num(visibleProducts.length)} товарам по текущему
                    фильтру
                  </span>
                )}
              </div>

              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn sm"
                  disabled={bulkMut.isPending || selection.targetIds.length === 0}
                  onClick={() =>
                    bulkMut.mutate({
                      productIds: selection.targetIds,
                      mode: 'best_offers',
                    })
                  }
                  title="Опубликовать товары и по 1 самому выгодному в-наличии SKU на каждый объём ГБ/дней (без дорогих дублей)"
                >
                  <AdminIcon name="check" size={13} />
                  <span>
                    {selection.usingExplicitSelection
                      ? `Включить выбранные (${num(selection.targetIds.length)}) · лучшие SKU`
                      : `Включить все (${num(selection.targetIds.length)}) · лучшие SKU`}
                  </span>
                </button>

                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={bulkMut.isPending || selection.targetIds.length === 0}
                  onClick={() =>
                    bulkMut.mutate({
                      productIds: selection.targetIds,
                      mode: 'all_offers',
                    })
                  }
                  title="Опубликовать товары и вообще все в-наличии SKU (включая альтернативные сети)"
                >
                  <span>Включить все SKU</span>
                </button>

                <button
                  type="button"
                  className="btn sm danger"
                  disabled={bulkMut.isPending || selection.targetIds.length === 0}
                  onClick={() =>
                    bulkMut.mutate({
                      productIds: selection.targetIds,
                      mode: 'unpublish_all',
                    })
                  }
                >
                  <span>Снять с витрины</span>
                </button>
              </div>
            </div>
          )}

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 38 }}>
                    <input
                      type="checkbox"
                      aria-label="Отметить все строки таблицы"
                      disabled={visibleProducts.length === 0}
                      checked={selection.allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = selection.someVisibleSelected;
                      }}
                      onChange={toggleSelectAllVisible}
                    />
                  </th>
                  <th>На витрине</th>
                  <th>Товар / Бренд</th>
                  <th>Провайдер & Режим</th>
                  <th>Наценка</th>
                  <th>SKU (актив./всего)</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {displayedProducts.map((prod) => {
                  const isOpen = Boolean(expandedProducts[prod.id]);
                  const isSelected = Boolean(selectedProducts[prod.id]);
                  const cat = categoriesBySlug.get(prod.categorySlug);
                  const publishedSkuCount = prod.offers.filter((o) => o.isPublished).length;

                  return (
                    <Fragment key={prod.id}>
                      <tr>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Выбрать строку ${prod.titleRu}`}
                            checked={isSelected}
                            onChange={(e) => toggleSelectOne(prod.id, e.target.checked)}
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Опубликовать ${prod.titleRu}`}
                            checked={prod.isPublished}
                            onChange={(e) => {
                              productMut.mutate({
                                id: prod.id,
                                patch: { isPublished: e.target.checked },
                              });
                            }}
                          />
                        </td>
                        <td>
                          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600 }}>{prod.titleRu}</span>
                            {prod.isHot && (
                              <span
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 700,
                                  padding: '1px 6px',
                                  borderRadius: 999,
                                  background: 'rgba(249, 115, 22, 0.18)',
                                  color: '#fb923c',
                                }}
                              >
                                🔥 HOT
                              </span>
                            )}
                          </div>
                          <div className="mono muted" style={{ fontSize: 11.5 }}>
                            /{prod.slug}
                            {prod.brandKey ? ` · hub:${prod.brandKey}` : ''}
                            {prod.countryIso ? ` · ${prod.countryIso}` : ''}
                          </div>
                        </td>
                        <td>
                          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                            <span className="chip info" style={{ fontSize: 11 }}>
                              {formatProviderBadgeLabel(prod.provider)}
                            </span>
                            <span className="muted mono" style={{ fontSize: 11.5 }}>
                              {prod.fulfillmentKind} · {prod.inputMode}
                            </span>
                          </div>
                        </td>
                        <td className="mono">
                          {prod.markupBp != null ? (
                            <span>{(prod.markupBp / 100).toFixed(1)}% (товар)</span>
                          ) : (
                            <span className="muted">
                              {((cat?.defaultMarkupBp ?? 1500) / 100).toFixed(1)}% (катег.)
                            </span>
                          )}
                        </td>
                        <td className="mono">
                          {publishedSkuCount} / {prod.offers.length}
                        </td>
                        <td>
                          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="btn sm"
                              onClick={() => toggleExpand(prod.id)}
                            >
                              {isOpen ? 'Скрыть SKU' : `SKU (${prod.offers.length})`}
                            </button>
                            <button
                              type="button"
                              className={prod.isHot ? 'btn sm primary' : 'btn sm ghost'}
                              title="Переключить бейдж 🔥 HOT на карточке бренда"
                              onClick={() =>
                                productMut.mutate({
                                  id: prod.id,
                                  patch: { isHot: !prod.isHot },
                                })
                              }
                            >
                              🔥
                            </button>
                            <button
                              type="button"
                              className="btn sm ghost"
                              onClick={() => setEditingProduct(prod)}
                            >
                              Карточка & Инструкции
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isOpen && prod.offers.length > 0 && (
                        <tr style={{ background: 'rgba(130, 148, 255, 0.06)' }}>
                          <td />
                          <td colSpan={6}>
                            <div
                              className="row"
                              style={{ justifyContent: 'space-between', gap: 8 }}
                            >
                              <span className="muted" style={{ fontSize: 12 }}>
                                Быстрое управление SKU для <strong>{prod.titleRu}</strong> (активно{' '}
                                {publishedSkuCount} из {prod.offers.length}):
                              </span>
                              <div className="row" style={{ gap: 6 }}>
                                <button
                                  type="button"
                                  className="btn sm success"
                                  disabled={bulkMut.isPending}
                                  onClick={() =>
                                    bulkMut.mutate({
                                      productIds: [prod.id],
                                      mode: 'best_offers',
                                    })
                                  }
                                >
                                  Лучшие SKU (без дублей)
                                </button>
                                <button
                                  type="button"
                                  className="btn sm ghost"
                                  disabled={bulkMut.isPending}
                                  onClick={() =>
                                    bulkMut.mutate({
                                      productIds: [prod.id],
                                      mode: 'all_offers',
                                    })
                                  }
                                >
                                  Включить все SKU
                                </button>
                                <button
                                  type="button"
                                  className="btn sm ghost"
                                  disabled={bulkMut.isPending}
                                  onClick={() =>
                                    bulkMut.mutate({
                                      productIds: [prod.id],
                                      mode: 'unpublish_all',
                                    })
                                  }
                                >
                                  Снять все SKU
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}

                      {isOpen &&
                        prod.offers.map((off) => {
                          const draft = offerDrafts[off.id] ?? {
                            titleRu: off.titleRu,
                            markupPct: bpToPercentStr(off.overrideMarkupBp),
                          };
                          const preview = computeAdminOfferPreview({
                            wholesaleUsdMicros: off.wholesaleUsdMicros,
                            usdRub,
                            gramRub,
                            offerOverrideMarkupBp: parsePercentInputToBp(draft.markupPct),
                            productMarkupBp: prod.markupBp,
                            categoryDefaultMarkupBp: cat?.defaultMarkupBp ?? 1500,
                            minAmountRub: off.minAmountRub,
                            providerRateBp: off.providerRateBp ?? null,
                          });

                          return (
                            <tr
                              key={off.id}
                              style={{ background: 'rgba(255, 255, 255, 0.025)' }}
                            >
                              <td />
                              <td style={{ paddingLeft: 18 }}>
                                <input
                                  type="checkbox"
                                  aria-label={`Опубликовать SKU ${off.titleRu}`}
                                  checked={off.isPublished}
                                  onChange={(e) =>
                                    offerMut.mutate({
                                      id: off.id,
                                      patch: { isPublished: e.target.checked },
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  value={draft.titleRu}
                                  onChange={(e) =>
                                    setOfferDrafts((prev) => ({
                                      ...prev,
                                      [off.id]: { ...draft, titleRu: e.target.value },
                                    }))
                                  }
                                  style={{ width: '100%', minWidth: 160 }}
                                />
                                <div className="mono muted" style={{ fontSize: 11, marginTop: 3 }}>
                                  SKU: {off.externalSku}
                                  {off.provider === 'LOCAL_STOCK' && (
                                    <span
                                      style={{
                                        color:
                                          (off.availableStockCount ?? 0) > 0
                                            ? '#38bdf8'
                                            : '#fbbf24',
                                        marginLeft: 8,
                                      }}
                                    >
                                      ● В пуле: {num(off.availableStockCount ?? 0)} шт.
                                    </span>
                                  )}
                                  {!off.inStock && (
                                    <span style={{ color: '#f87171', marginLeft: 8 }}>
                                      ●{' '}
                                      {off.provider === 'LOCAL_STOCK'
                                        ? off.isPublished
                                          ? 'Пул пуст (скрыт на витрине до загрузки кодов)'
                                          : 'Пул пуст'
                                        : 'Нет в наличии у провайдера'}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td colSpan={2}>
                                <div className="mono" style={{ fontSize: 12.5 }}>
                                  Опт: <strong>${preview.wholesaleUsd.toFixed(2)}</strong> (
                                  {preview.wholesaleRub.toFixed(2)} ₽) → Витрина:{' '}
                                  <strong>{preview.retailRub} ₽</strong> /{' '}
                                  <span>{preview.retailGram.toFixed(2)} GRAM</span>
                                </div>
                                <div
                                  className="mono"
                                  style={{
                                    fontSize: 11.5,
                                    marginTop: 2,
                                    color: preview.marginRub >= 0 ? '#34d399' : '#f87171',
                                  }}
                                >
                                  Маржа: {preview.marginRub >= 0 ? '+' : ''}
                                  {preview.marginRub.toFixed(2)} ₽ ({preview.marginPercent.toFixed(1)}%)
                                </div>
                              </td>
                              <td>
                                <div className="row" style={{ gap: 4 }}>
                                  <input
                                    style={{ width: 76 }}
                                    placeholder={bpToPercentStr(
                                      prod.markupBp ?? cat?.defaultMarkupBp ?? 1500,
                                    )}
                                    value={draft.markupPct}
                                    onChange={(e) =>
                                      setOfferDrafts((prev) => ({
                                        ...prev,
                                        [off.id]: { ...draft, markupPct: e.target.value },
                                      }))
                                    }
                                  />
                                  <span className="muted" style={{ fontSize: 12 }}>
                                    %
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                                  {off.provider === 'LOCAL_STOCK' && (
                                    <button
                                      type="button"
                                      className="btn sm primary"
                                      onClick={() =>
                                        setVoucherModalTarget({
                                          mode: 'upload',
                                          product: prod,
                                          offer: off,
                                        })
                                      }
                                    >
                                      📦 Коды ({num(off.availableStockCount ?? 0)})
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="btn sm"
                                    onClick={() =>
                                      offerMut.mutate({
                                        id: off.id,
                                        patch: {
                                          titleRu: draft.titleRu.trim() || off.titleRu,
                                          overrideMarkupBp: parsePercentInputToBp(draft.markupPct),
                                        },
                                      })
                                    }
                                  >
                                    Сохранить SKU
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </Fragment>
                  );
                })}
                {!visibleProducts.length && (
                  <tr>
                    <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                      Товары не найдены. Нажмите «Синхронизировать прайс», чтобы загрузить каталог провайдеров.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {visibleProducts.length > displayedProducts.length && (
            <div
              className="row"
              style={{
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderTop: '1px solid var(--hair)',
                background: 'var(--bg-2)',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <span className="muted" style={{ fontSize: 12.5 }}>
                Показано {num(displayedProducts.length)} из {num(visibleProducts.length)} товаров
              </span>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => setRenderLimit((prev) => prev + RENDER_LIMIT_STEP)}
                >
                  Показать ещё {num(RENDER_LIMIT_STEP)}
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  onClick={() => setRenderLimit(visibleProducts.length)}
                >
                  Показать все ({num(visibleProducts.length)})
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {editingProduct && (
        <ProductEditModal
          product={editingProduct}
          saving={productMut.isPending}
          onClose={() => setEditingProduct(null)}
          onSave={async (patch) => {
            await productMut.mutateAsync({ id: editingProduct.id, patch });
            setEditingProduct(null);
          }}
        />
      )}

      {voucherModalTarget && (
        <VoucherPoolModal
          target={voucherModalTarget}
          usdRub={usdRub}
          onClose={() => setVoucherModalTarget(null)}
          onCompleted={() => {
            void qc.invalidateQueries({ queryKey: ['admin-catalog-overview'] });
            void qc.invalidateQueries({ queryKey: ['admin-catalog-products'] });
          }}
        />
      )}
    </>
  );
  // END_BLOCK_ADMIN_CATALOG_PAGE
}

function ProductEditModal({
  product,
  saving,
  onClose,
  onSave,
}: {
  product: AdminCatalogProductDto;
  saving: boolean;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}) {
  // START_BLOCK_PRODUCT_EDIT_MODAL
  const [titleRu, setTitleRu] = useState(product.titleRu);
  const [titleEn, setTitleEn] = useState(product.titleEn);
  const [brandKey, setBrandKey] = useState(product.brandKey ?? '');
  const [descriptionRu, setDescriptionRu] = useState(product.descriptionRu ?? '');
  const [instructionsRu, setInstructionsRu] = useState(product.instructionsRu ?? '');
  const [instructionsEn, setInstructionsEn] = useState(product.instructionsEn ?? '');
  const [imageUrl, setImageUrl] = useState(product.imageUrl ?? '');
  const [markupPct, setMarkupPct] = useState(bpToPercentStr(product.markupBp));
  const [sortOrder, setSortOrder] = useState(String(product.sortOrder));
  const [isHot, setIsHot] = useState(Boolean(product.isHot));
  const [isPublished, setIsPublished] = useState(product.isPublished);

  const body = (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        style={{ maxWidth: 'min(640px, 100%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Настройка карточки: {product.titleRu}</h2>
          <span className="mono muted">/{product.slug}</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
              <span className="muted">Название (RU)</span>
              <input value={titleRu} onChange={(e) => setTitleRu(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
              <span className="muted">Название (EN)</span>
              <input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} />
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
              <span className="muted">Краткое описание (RU)</span>
              <input value={descriptionRu} onChange={(e) => setDescriptionRu(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
              <span className="muted">Ключ бренда (Brand Hub)</span>
              <input
                placeholder="apple / steam"
                value={brandKey}
                onChange={(e) => setBrandKey(e.target.value)}
              />
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
              <span className="muted">Индивидуальная наценка (%)</span>
              <input
                placeholder="По категории"
                value={markupPct}
                onChange={(e) => setMarkupPct(e.target.value)}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
              <span className="muted">Порядок сортировки</span>
              <input value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 14 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12.5,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                />
                <span>На витрине</span>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12.5,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={isHot}
                  onChange={(e) => setIsHot(e.target.checked)}
                />
                <span>🔥 HOT бейдж</span>
              </label>
            </div>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
            <span className="muted">URL иконки / обложки (опционально)</span>
            <input
              placeholder="https://..."
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
            <span className="muted">Инструкция по активации (RU — отправляется покупателю)</span>
            <textarea
              rows={3}
              value={instructionsRu}
              onChange={(e) => setInstructionsRu(e.target.value)}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
            <span className="muted">Activation Instructions (EN)</span>
            <textarea
              rows={2}
              value={instructionsEn}
              onChange={(e) => setInstructionsEn(e.target.value)}
            />
          </label>
        </div>

        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={saving}
            onClick={() =>
              void onSave({
                titleRu: titleRu.trim() || product.titleRu,
                titleEn: titleEn.trim() || product.titleEn,
                brandKey: brandKey.trim().toLowerCase() || null,
                descriptionRu: descriptionRu.trim() || null,
                instructionsRu: instructionsRu.trim() || null,
                instructionsEn: instructionsEn.trim() || null,
                imageUrl: imageUrl.trim() || null,
                markupBp: parsePercentInputToBp(markupPct),
                sortOrder: Math.max(0, Math.round(Number(sortOrder) || 0)),
                isHot,
                isPublished,
              })
            }
          >
            {saving ? 'Сохранение…' : 'Сохранить изменения'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(body, document.body);
  }
  return body;
  // END_BLOCK_PRODUCT_EDIT_MODAL
}
